// Derive the fleet / org command view state. Two feeds share one shape:
//   admin:  { instances, incidents, costs: { costs, plane, hosts }, hosts, pinnedKitRef }
//   org:    { apps (appInstance docs), orgCost (orgs/{org}/costs/{m}), usage, org }
// Output: nodes (one per instance), hosts (platform rings), center, totals,
// legend (tone buckets with counts). Every node carries `tone` (the colour
// it paints with), `issues[]` (what is wrong, one entry per kind) and the
// raw facts the inspector needs. `fleetInspector(state, telemetry, sel)`
// builds the DOM card for ANY selectable object on the stage — node, host,
// center, flow edge, incident shot, anomaly glyph — from the current feed,
// so a pinned card re-derives on every poll and stays live.
import { ago } from './ecosystemState';

const worst = (...l) => (l.includes('down') ? 'down' : l.includes('warn') ? 'warn' : l.includes('ok') ? 'ok' : 'off');
const usd = (v) => (v == null || !Number.isFinite(Number(v)) ? '—' : `$${Number(v).toFixed(2)}`);
const n0 = (v) => (v == null ? '—' : Math.round(Number(v)).toLocaleString());
const rate = (v) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)}k/min` : v >= 10 ? `${Math.round(v)}/min` : `${v.toFixed(1)}/min`);
const short = (v) => (v ? String(v).slice(0, 8) : '—');

// Tone buckets, in legend order. `busy` = transitional (provisioning,
// claimed, deploying, scanning); `idle` = live but no activity / suspended.
export const TONES = [
  { key: 'down', label: 'down' },
  { key: 'warn', label: 'degraded' },
  { key: 'busy', label: 'in progress' },
  { key: 'ok', label: 'healthy' },
  { key: 'idle', label: 'idle' },
  { key: 'pool', label: 'pool' },
];
const TONE_RANK = { down: 0, warn: 1, busy: 2, ok: 3, idle: 4, pool: 5 };
const toneOfLevel = (level) => (level === 'down' ? 'down' : level === 'warn' ? 'warn' : 'ok');

// The managed provision runs these in order (provisionWorker.js); optional
// stages (uat-link, assurance-seed…) are extra and never counted as missing.
export const PROVISION_STAGES = ['repo', 'host', 'usage-key', 'stack', 'edge', 'seed', 'verify', 'live'];
const SERVICES_STALE_MS = 15 * 60e3;

// Mirror of shared/appOperate.js serviceLevel / servicesSummary.
const serviceLevel = (s) => {
  if (s.state !== 'running') return s.kind === 'runner' || s.exitCode === 0 ? 'off' : 'down';
  if (s.health === 'unhealthy') return 'down';
  if (s.health === 'starting') return 'warn';
  return 'ok';
};
export function servicesOf(m, now = Date.now()) {
  if (!m?.services?.length || !m.servicesAt) return null;
  const stale = now - m.servicesAt > SERVICES_STALE_MS;
  const list = m.services.filter((s) => s.kind !== 'runner' && s.kind !== 'init').map((s) => ({ name: s.name, state: s.state, level: stale ? 'off' : serviceLevel(s) }));
  const counted = list.filter((s) => !(s.state !== 'running' && s.level === 'off'));
  return { at: m.servicesAt, stale, list, total: counted.length, up: stale ? 0 : counted.filter((s) => s.level === 'ok').length, down: stale ? [] : counted.filter((s) => s.level === 'down').map((s) => s.name) };
}
function provisioningOf(p) {
  if (!p) return null;
  const stages = p.stages || [];
  const done = PROVISION_STAGES.filter((k) => stages.some((s) => s.name === k && s.status === 'done')).length;
  const failed = stages.find((s) => s.status === 'failed' || s.status === 'error');
  return { stage: p.stage || null, status: p.status || null, done, total: PROVISION_STAGES.length, ratio: done / PROVISION_STAGES.length, failed: failed ? failed.name : null, stages, startedAt: stages.length ? Math.min(...stages.map((s) => s.at || Infinity)) : null };
}

function nodeOf(inst, { cost, incidents, pinnedKitRef, scanning, hostLoad }) {
  const mon = inst.monitor || null;
  const incidentsOpen = incidents.length;
  const outageOpen = incidents.filter((i) => i.kind !== 'anomaly').length;
  const anomalyOpen = incidentsOpen - outageOpen;
  // Probe/health incidents = down; anomaly incidents = warn on a reachable app.
  const monitorLevel = inst.status !== 'live' ? 'off' : outageOpen || mon?.state === 'down' ? 'down' : incidentsOpen || mon?.state === 'degraded' || (mon?.consecFails || 0) > 0 ? 'warn' : mon?.state === 'up' ? 'ok' : 'off';
  const counts = inst.scan?.counts || {};
  const scanLevel = scanning ? 'warn' : (counts.critical || 0) > 0 ? 'down' : (counts.high || 0) > 0 ? 'warn' : inst.scan?.lastScanAt ? 'ok' : 'off';
  const statusLevel = inst.status === 'live' ? 'ok' : inst.status === 'provisioning' || inst.status === 'claimed' ? 'warn' : inst.status === 'suspended' || inst.status === 'terminated' || inst.status === 'consumed' ? 'off' : 'warn';
  const level = inst.status !== 'live' ? statusLevel : worst(monitorLevel === 'off' ? 'ok' : monitorLevel, scanLevel === 'off' ? 'ok' : scanLevel);
  const external = inst.delivery === 'external' || (!!inst.deployment?.target && inst.deployment.target !== 'managed');
  const kitRef = inst.repo?.kitRef || inst.kitRef || null;
  const pool = inst.warmPool === true;
  const kitDrift = !!(kitRef && pinnedKitRef && !external && kitRef !== pinnedKitRef);
  const tlsDaysLeft = mon?.tlsDaysLeft ?? null;
  const latencyMs = mon?.lastLatencyMs ?? null;
  const idle = !!cost?.idle;
  const neverActive = !!cost?.neverActive;
  const services = servicesOf(mon);
  const provisioning = provisioningOf(inst.provisioning);
  const deploy = inst.deploy || null;
  const heal = mon?.heal || null;
  const recentRollback = !!(deploy?.lastIsRollback && deploy.lastProdAt && Date.now() - deploy.lastProdAt < 24 * 3600e3);
  const phase = pool ? 'pool' : inst.status === 'live' ? 'live' : inst.status === 'provisioning' ? 'provisioning' : inst.status === 'claimed' ? 'claimed' : inst.status === 'suspended' ? 'suspended' : inst.status;

  // Issues: one entry per kind, worst first. Transitional kinds are
  // `busy`, not a fault — they drive the tone only when nothing is wrong.
  const issues = [];
  if (inst.status === 'live') {
    if (outageOpen || mon?.state === 'down') issues.push({ kind: 'probe', level: 'down', text: outageOpen ? `probe failing · ${outageOpen} open incident${outageOpen > 1 ? 's' : ''}` : 'probe failing', word: outageOpen ? `down · ${outageOpen} incident${outageOpen > 1 ? 's' : ''}` : 'probe failing' });
    else if (mon?.state === 'degraded' || (mon?.consecFails || 0) > 0) issues.push({ kind: 'probe', level: 'warn', text: mon?.state === 'degraded' ? 'degraded' : `${mon.consecFails} consecutive probe fail${mon.consecFails > 1 ? 's' : ''}`, word: mon?.state === 'degraded' ? 'degraded' : `${mon.consecFails} probe fail${mon.consecFails > 1 ? 's' : ''}` });
    if (services?.down?.length) issues.push({ kind: 'service', level: 'down', text: `${services.down.join(', ')} down inside the stack`, word: `${services.down.length} svc down` });
    else if (services?.stale) issues.push({ kind: 'silent', level: 'warn', text: `stack silent · last report ${ago(services.at)}`, word: 'stack silent' });
    if (anomalyOpen) issues.push({ kind: 'incident', level: 'warn', text: `${anomalyOpen} anomaly incident${anomalyOpen > 1 ? 's' : ''} open`, word: `${anomalyOpen} anomal${anomalyOpen > 1 ? 'ies' : 'y'}` });
    if (scanning) issues.push({ kind: 'scanning', level: 'busy', text: 'security scan running', word: 'scanning' });
    else if ((counts.critical || 0) > 0) issues.push({ kind: 'scan', level: 'down', text: `${counts.critical} critical · ${counts.high || 0} high findings`, word: `${counts.critical} critical` });
    else if ((counts.high || 0) > 0) issues.push({ kind: 'scan', level: 'warn', text: `${counts.high} high finding${counts.high > 1 ? 's' : ''}`, word: `${counts.high} high` });
    if (tlsDaysLeft != null && tlsDaysLeft < 14) issues.push({ kind: 'tls', level: tlsDaysLeft < 3 ? 'down' : 'warn', text: `TLS expires in ${Math.max(0, Math.round(tlsDaysLeft))}d`, word: `TLS ${Math.max(0, Math.round(tlsDaysLeft))}d` });
    if (latencyMs != null && latencyMs > 1500) issues.push({ kind: 'latency', level: 'warn', text: `slow probe · ${Math.round(latencyMs)} ms`, word: `slow · ${Math.round(latencyMs)} ms` });
    if (recentRollback) issues.push({ kind: 'rollback', level: 'warn', text: `rolled back ${ago(deploy.lastProdAt)}`, word: 'rolled back' });
    if (kitDrift) issues.push({ kind: 'drift', level: 'warn', text: `kit ${short(kitRef)} · pinned ${short(pinnedKitRef)}`, word: 'kit drift' });
    if (mon?.versionBehind) issues.push({ kind: 'version', level: 'warn', text: 'kit version behind', word: 'kit behind' });
    if (!pool && neverActive) issues.push({ kind: 'idle', level: 'idle', text: 'never active', word: 'never active' });
    else if (!pool && idle) issues.push({ kind: 'idle', level: 'idle', text: `idle 30d${cost?.lastActivityAt ? ` · last ${ago(cost.lastActivityAt)}` : ''}`, word: 'idle' });
  } else if (inst.status === 'provisioning') {
    if (provisioning?.failed) issues.push({ kind: 'provisioning', level: 'down', text: `provisioning failed at ${provisioning.failed}`, word: `failed · ${provisioning.failed}` });
    else issues.push({ kind: 'provisioning', level: 'busy', text: provisioning?.stage ? `provisioning · ${provisioning.stage} (${provisioning.done}/${provisioning.total})` : inst.statusDetail || 'provisioning', word: provisioning?.stage ? `${provisioning.stage} ${provisioning.done}/${provisioning.total}` : 'provisioning' });
  } else if (inst.status === 'claimed') issues.push({ kind: 'claimed', level: 'busy', text: inst.statusDetail || 'claimed · waiting for the stack', word: 'claimed' });
  else if (inst.status === 'suspended') issues.push({ kind: 'suspended', level: 'idle', text: inst.statusDetail || 'suspended', word: 'suspended' });
  else issues.push({ kind: inst.status, level: 'busy', text: inst.statusDetail || inst.status, word: String(inst.status).replace('_', ' ') });
  const rank = { down: 0, warn: 1, busy: 2, idle: 3 };
  issues.sort((a, b) => (rank[a.level] ?? 9) - (rank[b.level] ?? 9));

  // A warm-pool stack that is still being built is work in progress; once
  // it is waiting (claimed / live) it is just a dim pool slot.
  const tone = pool && inst.status !== 'provisioning' ? 'pool' : issues.length ? issues[0].level : 'ok';
  const name = inst.name || inst.slug;

  return {
    id: `${inst.orgId || ''}/${inst.appId || inst.id}`,
    appId: inst.appId || inst.id,
    orgId: inst.orgId || null,
    slug: inst.slug,
    name,
    status: inst.status,
    statusDetail: inst.statusDetail || '',
    phase,
    level,
    tone,
    issues,
    // One short phrase for the stage label: the worst issue, else the
    // probe state. Traffic words come from telemetry in the scene.
    word: pool ? 'pool' : issues[0]?.word || (inst.status === 'live' ? (mon?.state === 'up' ? 'up' : 'live') : String(inst.status).replace('_', ' ')),
    monitorLevel,
    monitorState: mon?.state || null,
    consecFails: mon?.consecFails || 0,
    scanLevel,
    scanning: !!scanning,
    scanCounts: { critical: counts.critical || 0, high: counts.high || 0, medium: counts.medium || 0, low: counts.low || 0 },
    lastScanAt: inst.scan?.lastScanAt || null,
    incidentsOpen,
    incidents,
    external,
    pool,
    host: inst.host ? String(inst.host) : null,
    workspaceId: inst.workspaceId || null,
    envRole: inst.envRole || 'prod',
    uatAppId: inst.uatAppId || null,
    plan: inst.plan || null,
    region: inst.region || null,
    version: inst.version || null,
    assurance: inst.assurance === true,
    kitRef,
    kitDrift,
    versionBehind: !!mon?.versionBehind,
    services,
    provisioning,
    deploy: deploy ? { lastProdAt: deploy.lastProdAt || null, lastProdSha: deploy.lastProdSha || null, lastProdRef: deploy.lastProdRef || null, lastIsRollback: !!deploy.lastIsRollback } : null,
    heal: heal ? { lastAt: heal.lastAt || heal.at || null, count: heal.count ?? heal.today ?? null, last: heal.last || heal.lastService || null } : null,
    license: inst.license ? { status: inst.license.status, lastSeenAt: inst.license.lastSeenAt || null } : null,
    mtdUsd: cost?.mtdUsd ?? null,
    burnPerDayUsd: cost?.burnPerDayUsd ?? null,
    projectedUsd: cost?.projectedUsd ?? null,
    marginUsd: cost?.marginUsd ?? null,
    revenueUsd: cost?.revenueUsd ?? null,
    idle,
    neverActive,
    lastActivityAt: cost?.lastActivityAt || inst.monitor?.lastActivityAt || null,
    memMb: hostLoad?.memMb ?? null,
    tlsDaysLeft,
    latencyMs,
    lastCheckAt: mon?.lastCheckAt || 0,
    url: inst.urls?.app || null,
    deployRunning: null,
  };
}

export function deriveFleet(data) {
  const d = data || {};
  const mode = d.mode || (d.instances ? 'admin' : 'org');
  const instances = d.instances || (d.apps || []).map((a) => ({ ...a, appId: a.id, orgId: d.org?.id || null }));
  const costs = d.costs?.costs || {};
  const planeCost = d.costs?.plane || null;
  const hostsLoad = d.costs?.hosts || {};
  const openBySlug = {};
  for (const i of d.incidents || []) {
    if (i.status !== 'open') continue;
    (openBySlug[i.slug] = openBySlug[i.slug] || []).push({ id: i.id, kind: i.kind || 'probe', reason: i.reason || '', openedAt: i.openedAt || null, slug: i.slug });
  }
  const pinnedKitRef = d.pinnedKitRef || null;

  // Org mode: per-app cost from the rollup's byApp.
  const byApp = d.orgCost?.byApp || {};

  const nodes = instances
    .filter((i) => i.status !== 'terminated' && i.status !== 'consumed')
    .map((i) => nodeOf(i, {
      cost: mode === 'admin' ? costs[i.slug] : (byApp[i.appId] ? { mtdUsd: byApp[i.appId].mtdUsd, burnPerDayUsd: byApp[i.appId].burnPerDayUsd, projectedUsd: byApp[i.appId].projectedUsd, lastActivityAt: byApp[i.appId].lastActivityAt } : null),
      incidents: openBySlug[i.slug] || [],
      pinnedKitRef,
      scanning: !!i.scanRunning,
      hostLoad: i.host ? hostsLoad[String(i.host)]?.load?.[i.slug] : null,
    }));

  const maxCost = Math.max(1, ...nodes.map((n) => n.mtdUsd || 0));
  const maxMem = Math.max(1, ...nodes.map((n) => n.memMb || 0));
  for (const n of nodes) {
    n.size = 0.22 + 0.5 * Math.sqrt((n.mtdUsd || 0) / maxCost);
    // Shares for the two vital bars beside every node (cost of the costliest, memory of the hungriest).
    n.costRatio = n.mtdUsd ? n.mtdUsd / maxCost : 0;
    n.memRatio = n.memMb ? n.memMb / maxMem : 0;
  }

  const hosts = (d.hosts || []).map((h) => {
    const id = h.hostId || h.id;
    const stats = h.stats || null;
    const agentStale = !!(h.agentAt && Date.now() - h.agentAt > 10 * 60e3);
    const issues = [];
    if (h.status && !['active', 'ready', 'live', 'ok'].includes(h.status)) issues.push({ kind: 'status', level: 'warn', text: `host ${h.status}` });
    if (stats && Number(stats.diskPct) >= 85) issues.push({ kind: 'disk', level: Number(stats.diskPct) >= 95 ? 'down' : 'warn', text: `disk ${Math.round(Number(stats.diskPct))}%` });
    if (stats && Number(stats.memUsedPct) >= 90) issues.push({ kind: 'mem', level: 'warn', text: `mem ${Math.round(Number(stats.memUsedPct))}%` });
    if (agentStale) issues.push({ kind: 'agent', level: 'warn', text: `agent silent ${ago(h.agentAt)}` });
    issues.sort((a, b) => (a.level === 'down' ? 0 : 1) - (b.level === 'down' ? 0 : 1));
    const level = issues.some((x) => x.level === 'down') ? 'down' : issues.length ? 'warn' : 'ok';
    const hostNodes = nodes.filter((n) => n.host === id);
    return {
      id, level, tone: level, issues, agentStale, agentAt: h.agentAt || null, status: h.status || '', region: h.region || '', capacity: Number(h.capacity) || 8,
      count: h.count || hostNodes.length,
      diskPct: stats ? Number(stats.diskPct) || null : null, memUsedPct: stats ? Number(stats.memUsedPct) || null : null, load1: stats ? stats.load1 ?? null : null,
      load: hostsLoad[id]?.load || {},
      nodes: hostNodes,
    };
  });
  // Instances whose host is not in the hosts list (or none) still get drawn.
  const hostIds = new Set(hosts.map((h) => h.id));
  const unhosted = nodes.filter((n) => !n.external && (!n.host || !hostIds.has(n.host)));
  const external = nodes.filter((n) => n.external);

  const live = nodes.filter((n) => n.status === 'live');
  // Cost totals sum over EVERY instance (incl. terminated ones that still
  // carry this month's spend) so they equal the Costs tab strip.
  const costOf = (i) => (mode === 'admin' ? costs[i.slug] : byApp[i.appId]);
  const sum = (k) => instances.reduce((a, i) => a + (Number(costOf(i)?.[k]) || 0), 0);
  const totals = {
    instances: nodes.length,
    live: live.length,
    provisioning: nodes.filter((n) => n.status === 'provisioning').length,
    down: nodes.filter((n) => n.monitorLevel === 'down').length,
    incidents: Object.values(openBySlug).reduce((a, b) => a + b.length, 0),
    scanning: nodes.filter((n) => n.scanning).length,
    findingsHigh: nodes.reduce((a, n) => a + (n.scanLevel === 'down' || n.scanLevel === 'warn' ? 1 : 0), 0),
    idle: nodes.filter((n) => n.idle || n.neverActive).length,
    drift: nodes.filter((n) => n.kitDrift).length,
    appsMtdUsd: sum('mtdUsd'),
    appsBurnUsd: sum('burnPerDayUsd'),
    appsProjectedUsd: sum('projectedUsd'),
    revenueUsd: sum('revenueUsd'),
    hostsOk: hosts.filter((h) => h.level === 'ok').length,
    hosts: hosts.length,
  };

  const legend = TONES.map((t) => ({ ...t, count: nodes.filter((n) => n.tone === t.key).length }));

  // Center: control plane (admin) or the org (org mode) with its own cost arc.
  const oc = d.orgCost || null;
  const limitUsd = Number(d.org?.costLimits?.monthlyUsd) || null;
  const centerMtd = mode === 'admin' ? (planeCost?.totalUsd ?? null) : (oc?.mtdUsd ?? null);
  const centerProj = mode === 'admin' ? (planeCost?.projectedUsd ?? null) : (oc?.projectedUsd ?? null);
  const centerBurn = mode === 'admin' ? (planeCost?.burnPerDayUsd ?? null) : (oc?.burnPerDayUsd ?? null);
  const denom = mode === 'org' && limitUsd ? limitUsd : (centerProj || centerMtd || 1);
  const centerLevel = mode === 'org' && limitUsd && centerMtd != null ? (centerMtd >= limitUsd ? 'down' : centerMtd >= limitUsd * 0.8 || (centerProj || 0) > limitUsd ? 'warn' : 'ok') : 'ok';
  const overall = worst(...nodes.filter((n) => n.status === 'live').map((n) => n.level), ...hosts.map((h) => h.level), 'ok');
  const center = {
    label: mode === 'admin' ? 'nventr control plane' : (d.org?.name || 'your organization'),
    mtdUsd: centerMtd,
    burnPerDayUsd: centerBurn,
    projectedUsd: centerProj,
    limitUsd: mode === 'org' ? limitUsd : null,
    ratio: centerMtd != null ? centerMtd / denom : 0,
    ghost: centerProj != null ? centerProj / denom : null,
    ticks: mode === 'org' && limitUsd ? [0.5, 0.8, 1] : [],
    level: centerLevel,
    cometSpeed: centerBurn > 0 ? Math.min(0.35, 0.03 + (centerBurn || 0) / 60) : 0,
    byService: planeCost?.byService || null,
    pulledAt: planeCost?.pulledAt || oc?.updatedAt || null,
    alert: d.org?.costAlertState || null,
    platformUsd: oc?.platform?.usd ?? null,
    // Second line under the center label: the fleet in one glance.
    summary: mode === 'admin'
      ? `${live.length} live · ${hosts.filter((h) => h.level === 'ok').length}/${hosts.length} hosts${centerMtd != null ? ` · plane ${usd(centerMtd)} mtd` : ''}`
      : `${nodes.filter((n) => !n.pool).length} app${nodes.filter((n) => !n.pool).length === 1 ? '' : 's'} · ${live.length} live${centerMtd != null ? ` · ${usd(centerMtd)} mtd` : ''}`,
    byApp: mode === 'org' ? Object.entries(byApp).map(([id, a]) => ({ id, name: a.name || id, mtdUsd: a.mtdUsd })).sort((a, b) => (b.mtdUsd || 0) - (a.mtdUsd || 0)) : nodes.filter((n) => n.mtdUsd).map((n) => ({ id: n.id, name: n.name, mtdUsd: n.mtdUsd })).sort((a, b) => b.mtdUsd - a.mtdUsd),
    overall,
  };

  const events = [];
  for (const i of (d.incidents || []).slice(0, 20)) {
    if (i.openedAt) events.push({ at: i.openedAt, level: 'down', text: `${i.slug} · incident opened · ${i.reason || 'probe failed'}` });
    if (i.closedAt) events.push({ at: i.closedAt, level: 'ok', text: `${i.slug} · incident closed` });
  }
  for (const n of nodes) if (n.scanning) events.push({ at: Date.now(), level: 'busy', text: `${n.slug} · security scan running` });
  events.sort((a, b) => b.at - a.at);

  const notes = [];
  if (totals.incidents) notes.push(`${totals.incidents} open incident${totals.incidents > 1 ? 's' : ''}`);
  if (totals.down) notes.push(`${totals.down} down`);
  if (totals.scanning) notes.push(`${totals.scanning} scanning`);
  if (totals.provisioning) notes.push(`${totals.provisioning} provisioning`);
  if (mode === 'admin' && totals.idle) notes.push(`${totals.idle} idle`);
  if (mode === 'admin' && totals.drift) notes.push(`${totals.drift} off the pinned kit`);
  if (center.alert && center.alert.level !== 'ok') notes.push(center.alert.message);

  return {
    mode, nodes, hosts, unhosted, external, totals, center, events, overall, legend,
    caption: notes.length ? notes.join(' · ') : `all quiet · ${totals.live} live · ${center.mtdUsd != null ? `${usd(center.mtdUsd)} mtd` : 'no cost data yet'}${center.pulledAt ? ` · ${ago(center.pulledAt)}` : ''}`,
  };
}

// ---- inspector cards ------------------------------------------------------------
// sel = { kind: 'node'|'host'|'center'|'flow'|'incident'|'anomaly', id }.
// Card = { kind, id, title, subtitle, tone, rows: [[k, v]], sparks: [{ label,
// values, color, last }], sections: [{ title, rows }], issues: [{ level, text }],
// open: drawerId|null, children: [sel…] (what to focus on the stage) }.
const tail = (arr, k) => (Array.isArray(arr) ? arr.slice(-k) : []);
const SPARK_N = 24;

function nodeCard(s, t, n) {
  const live = t?.live?.[n.slug] || null;
  const series = t?.series?.[n.slug] || null;
  const flowsOut = (t?.flows || []).filter((f) => f.from === n.slug);
  const flowsIn = (t?.flows || []).filter((f) => f.to === n.slug);
  const anomalies = (t?.anomalies || []).filter((a) => a.slug === n.slug && a.closedAt == null);
  const rt = t?.runtime?.[n.slug] || null;
  const plane = t?.planeCalls?.[n.slug] || null;
  const sim = t?.source === 'sim';
  const sparks = [];
  if (series && series.req) {
    sparks.push({ label: `requests / min${sim ? ' · sim' : ''}`, values: tail(series.req, SPARK_N), color: 'ok', last: live ? rate(live.req) : null });
    if (tail(series.s5xx, SPARK_N).some(Boolean)) sparks.push({ label: '5xx / min', values: tail(series.s5xx, SPARK_N), color: 'down', last: live ? rate(live.s5xx) : null });
    if (tail(series.p95, SPARK_N).some(Boolean)) sparks.push({ label: 'p95 latency', values: tail(series.p95, SPARK_N), color: 'warn', last: live?.p95 ? `${Math.round(live.p95)} ms` : null });
  }
  const sections = [];
  if (n.provisioning) {
    sections.push({ title: `provisioning · ${n.provisioning.done}/${n.provisioning.total}`, rows: PROVISION_STAGES.map((k) => { const st = n.provisioning.stages.find((x) => x.name === k); return [k, st ? (st.status === 'done' ? `done${st.at ? ` · ${ago(st.at)}` : ''}` : st.status) : (n.provisioning.stage === k ? 'running' : '—')]; }) });
  }
  if (n.services?.list?.length) {
    sections.push({ title: `services · ${n.services.stale ? `silent ${ago(n.services.at)}` : `${n.services.up}/${n.services.total} up · ${ago(n.services.at)}`}`, dots: n.services.list.map((x) => ({ level: x.level, text: x.name })) });
  }
  if (flowsOut.length || flowsIn.length) {
    sections.push({ title: `inter-app flows${sim ? ' · sim' : ''}`, rows: [...flowsOut.slice(0, 4).map((f) => [`→ ${f.to}`, rate(f.ratePerMin)]), ...flowsIn.slice(0, 4).map((f) => [`← ${f.from}`, rate(f.ratePerMin)])] });
  }
  if (rt?.totals && (rt.totals.queued || rt.totals.running || rt.totals.stale)) {
    sections.push({ title: `queues · ${ago(rt.at)}`, rows: [['queued', n0(rt.totals.queued)], ['running', n0(rt.totals.running)], rt.totals.stale ? ['stale claims', n0(rt.totals.stale)] : null].filter(Boolean) });
  }
  const issues = [...n.issues, ...anomalies.slice(0, 3).map((a) => ({ kind: 'anomaly', level: a.severity === 'down' ? 'down' : 'warn', text: a.text || `${a.metric} ${a.kind}` }))];
  return {
    kind: 'node', id: n.id, open: n.id,
    title: n.pool ? `pool · ${n.slug}` : n.name,
    subtitle: [n.slug !== n.name ? n.slug : null, n.external ? 'customer-hosted' : n.host ? `host …${String(n.host).slice(-6)}` : null, n.envRole !== 'prod' ? n.envRole : null, n.plan || null, n.region || null].filter(Boolean).join(' · '),
    tone: n.tone,
    rows: [
      ['status', `${n.status}${n.monitorLevel !== 'off' ? ` · ${n.monitorLevel === 'ok' ? 'up' : n.monitorLevel}` : ''}${n.consecFails ? ` · ${n.consecFails} fails` : ''}`],
      n.latencyMs != null ? ['probe', `${Math.round(n.latencyMs)} ms${n.lastCheckAt ? ` · ${ago(n.lastCheckAt)}` : ''}`] : null,
      live ? ['traffic now', `${rate(live.req)} · ${n0(live.s5xx)} 5xx · p95 ${live.p95 ? `${Math.round(live.p95)} ms` : '—'}`] : null,
      live && (live.tokens || live.convos) ? ['agent now', `${n0(live.convos)} convos · ${n0(live.tokens)} tokens`] : null,
      plane?.ratePerMin ? ['plane calls', rate(plane.ratePerMin)] : null,
      n.tlsDaysLeft != null ? ['TLS', `${Math.round(n.tlsDaysLeft)}d left`] : null,
      n.mtdUsd != null ? ['cost mtd / proj', `${usd(n.mtdUsd)} / ${usd(n.projectedUsd)}`] : null,
      n.burnPerDayUsd != null ? ['burn / day', usd(n.burnPerDayUsd)] : null,
      n.revenueUsd != null ? ['revenue · margin', `${usd(n.revenueUsd)} · ${usd(n.marginUsd)}`] : null,
      n.lastScanAt ? ['scan', n.scanning ? 'running' : `${n.scanCounts.critical}c ${n.scanCounts.high}h ${n.scanCounts.medium}m · ${ago(n.lastScanAt)}`] : null,
      n.kitRef || n.version ? ['kit', `${short(n.kitRef)}${n.version ? ` · v${String(n.version).slice(0, 12)}` : ''}${n.kitDrift ? ' · off pin' : ''}`] : null,
      n.deploy?.lastProdAt ? ['last deploy', `${ago(n.deploy.lastProdAt)}${n.deploy.lastIsRollback ? ' · rollback' : ''}${n.deploy.lastProdSha ? ` · ${short(n.deploy.lastProdSha)}` : ''}`] : null,
      n.heal?.lastAt ? ['self-heal', `${n.heal.last ? `${n.heal.last} · ` : ''}${ago(n.heal.lastAt)}`] : null,
      n.license ? ['license', `${n.license.status}${n.license.lastSeenAt ? ` · seen ${ago(n.license.lastSeenAt)}` : ''}`] : null,
      n.lastActivityAt ? ['last activity', ago(n.lastActivityAt)] : null,
      n.memMb != null ? ['memory', `${n0(n.memMb)} MB`] : null,
    ].filter(Boolean),
    sparks, sections, issues,
    children: [...flowsOut, ...flowsIn].map((f) => ({ kind: 'flow', id: `${f.from}>${f.to}` })),
  };
}

function hostCard(s, t, h) {
  const bars = h.nodes.map((n) => ({ label: n.slug, value: n.memMb || 0 }));
  const req = h.nodes.reduce((a, n) => a + (t?.live?.[n.slug]?.req || 0), 0);
  const sparks = [];
  const sum = sumSeries(t, h.nodes.map((n) => n.slug));
  if (sum) sparks.push({ label: `requests / min · ${h.nodes.length} apps`, values: sum, color: 'ok', last: rate(req) });
  return {
    kind: 'host', id: h.id, open: 'hosts',
    title: `host …${String(h.id).slice(-6)}`,
    subtitle: [h.region || null, h.status || null, `${h.count}/${h.capacity} slots`].filter(Boolean).join(' · '),
    tone: h.tone,
    rows: [
      h.memUsedPct != null ? ['memory', `${Math.round(h.memUsedPct)}%`] : null,
      h.diskPct != null ? ['disk', `${Math.round(h.diskPct)}%`] : null,
      h.load1 != null ? ['load (1m)', String(h.load1)] : null,
      h.agentAt ? ['agent', ago(h.agentAt)] : null,
      ['apps', `${h.nodes.filter((n) => n.tone === 'ok').length} healthy · ${h.nodes.filter((n) => n.tone === 'down' || n.tone === 'warn').length} need attention`],
    ].filter(Boolean),
    sparks,
    sections: [
      bars.some((b) => b.value) ? { title: 'memory per app (MB)', bars } : null,
      { title: 'apps on this host', dots: h.nodes.map((n) => ({ level: n.tone, text: `${n.slug}${n.issues[0] ? ` · ${n.issues[0].kind}` : ''}` })) },
    ].filter(Boolean),
    issues: h.issues,
    children: h.nodes.map((n) => ({ kind: 'node', id: n.id })),
  };
}

function sumSeries(t, slugs) {
  const list = slugs.map((sl) => t?.series?.[sl]?.req).filter(Boolean);
  if (!list.length) return null;
  const n = Math.min(SPARK_N, ...list.map((a) => a.length));
  const out = new Array(n).fill(0);
  for (const a of list) { const tl = a.slice(-n); for (let i = 0; i < n; i += 1) out[i] += tl[i] || 0; }
  return out;
}

function centerCard(s, t) {
  const c = s.center; const tt = s.totals;
  const attention = s.nodes.filter((n) => n.tone === 'down' || n.tone === 'warn').sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]);
  const sim = t?.source === 'sim';
  const all = sumSeries(t, s.nodes.map((n) => n.slug));
  const sparks = all ? [{ label: `fleet requests / min${sim ? ' · sim' : ''}`, values: all, color: 'ok', last: rate(Object.values(t.live || {}).reduce((a, x) => a + (x.req || 0), 0)) }] : [];
  const sections = [];
  if (s.mode === 'admin' && c.byService) sections.push({ title: 'plane cost by service · mtd', rows: Object.entries(c.byService).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, v]) => [k, usd(v)]) });
  if (c.byApp?.length) sections.push({ title: 'top apps by cost · mtd', rows: c.byApp.slice(0, 5).map((a) => [a.name, usd(a.mtdUsd)]) });
  return {
    kind: 'center', id: 'center', open: s.mode === 'admin' ? 'plane' : null,
    title: c.label,
    subtitle: `${tt.live} live · ${tt.instances} total${s.hosts.length ? ` · ${tt.hostsOk}/${tt.hosts} hosts healthy` : ''}${s.external.length ? ` · ${s.external.length} customer-hosted` : ''}`,
    tone: toneOfLevel(c.overall === 'off' ? 'ok' : c.overall),
    rows: [
      c.mtdUsd != null ? ['cost mtd', usd(c.mtdUsd)] : null,
      c.projectedUsd != null ? ['projected', usd(c.projectedUsd)] : null,
      c.burnPerDayUsd != null ? ['burn / day', usd(c.burnPerDayUsd)] : null,
      s.mode === 'org' && c.limitUsd ? ['limit', `${usd(c.limitUsd)} · ${Math.round((c.mtdUsd || 0) / c.limitUsd * 100)}%`] : null,
      s.mode === 'admin' && tt.appsMtdUsd ? ['attributed to apps', usd(tt.appsMtdUsd)] : null,
      s.mode === 'admin' && tt.revenueUsd ? ['app revenue · margin', `${usd(tt.revenueUsd)} · ${usd(tt.revenueUsd - tt.appsMtdUsd)}`] : null,
      t?.totals ? [`traffic · ${t.window?.label || '24h'}`, `${n0(t.totals.req)} req · ${n0(t.totals.s5xx)} 5xx · p95 ${t.totals.p95 ? `${Math.round(t.totals.p95)} ms` : '—'}`] : null,
      tt.incidents || tt.scanning || tt.provisioning ? ['in flight', [tt.incidents ? `${tt.incidents} incident${tt.incidents > 1 ? 's' : ''}` : null, tt.scanning ? `${tt.scanning} scanning` : null, tt.provisioning ? `${tt.provisioning} provisioning` : null].filter(Boolean).join(' · ')] : null,
      c.pulledAt ? ['updated', ago(c.pulledAt)] : null,
    ].filter(Boolean),
    sparks, sections,
    issues: attention.length ? attention.slice(0, 6).map((n) => ({ kind: n.issues[0]?.kind || 'issue', level: n.tone, text: `${n.slug} · ${n.issues[0]?.text || n.tone}` })) : [{ kind: 'quiet', level: 'ok', text: 'nothing needs attention' }],
    children: attention.map((n) => ({ kind: 'node', id: n.id })),
  };
}

function flowCard(s, t, id) {
  const f = (t?.flows || []).find((x) => `${x.from}>${x.to}` === id);
  if (!f) return null;
  const a = s.nodes.find((n) => n.slug === f.from); const b = s.nodes.find((n) => n.slug === f.to);
  const sim = t?.source === 'sim';
  return {
    kind: 'flow', id, open: 'traffic',
    title: `${f.from} → ${f.to}`,
    subtitle: `${f.kind || 'http'} flow${sim ? ' · simulated' : ''} · ${t.window?.label || ''}`,
    tone: 'ok',
    rows: [
      ['rate', rate(f.ratePerMin)],
      ['calls in window', n0(f.n)],
      a ? [f.from, `${a.tone}${t.live?.[f.from] ? ` · ${rate(t.live[f.from].req)}` : ''}`] : null,
      b ? [f.to, `${b.tone}${t.live?.[f.to] ? ` · ${rate(t.live[f.to].req)}` : ''}`] : null,
    ].filter(Boolean),
    sparks: [], sections: [], issues: [],
    children: [a, b].filter(Boolean).map((n) => ({ kind: 'node', id: n.id })),
  };
}

function incidentCard(s, t, id) {
  const n = s.nodes.find((x) => x.incidents.some((i) => i.id === id));
  const inc = n?.incidents.find((i) => i.id === id);
  if (!inc) return null;
  return {
    kind: 'incident', id, open: 'incidents',
    title: `incident · ${inc.slug}`,
    subtitle: `${inc.kind} · opened ${ago(inc.openedAt)}`,
    tone: inc.kind === 'anomaly' ? 'warn' : 'down',
    rows: [
      ['reason', inc.reason || 'probe failed'],
      ['open for', inc.openedAt ? ago(inc.openedAt).replace(' ago', '') : '—'],
      n ? ['app now', `${n.status}${n.monitorLevel !== 'off' ? ` · ${n.monitorLevel}` : ''}${n.latencyMs != null ? ` · ${Math.round(n.latencyMs)} ms` : ''}`] : null,
    ].filter(Boolean),
    sparks: [], sections: [], issues: n ? n.issues : [],
    children: n ? [{ kind: 'node', id: n.id }] : [],
  };
}

function anomalyCard(s, t, id) {
  const a = (t?.anomalies || []).find((x) => x.id === id);
  if (!a) return null;
  const n = s.nodes.find((x) => x.slug === a.slug);
  const series = t?.series?.[a.slug];
  const key = a.metric === 'p95' ? 'p95' : a.metric === '5xx' ? 's5xx' : 'req';
  return {
    kind: 'anomaly', id, open: 'anomalies',
    title: `${a.slug} · ${a.metric === 'p95' ? 'latency' : a.metric} ${a.kind}`,
    subtitle: `${a.severity === 'down' ? 'severe' : 'warning'} · opened ${ago(a.openedAt)}${a.closedAt ? ' · closed' : ''}${t?.source === 'sim' ? ' · simulated' : ''}`,
    tone: a.severity === 'down' ? 'down' : 'warn',
    rows: [
      ['now vs baseline', `${a.metric === 'p95' ? `${n0(a.value)} ms` : rate(a.value)} vs ${a.metric === 'p95' ? `${n0(a.baseline)} ms` : rate(a.baseline)}`],
      a.z ? ['z-score', Number(a.z).toFixed(1)] : null,
      n ? ['app', `${n.status} · ${n.tone}`] : null,
    ].filter(Boolean),
    sparks: series?.[key] ? [{ label: a.metric === 'p95' ? 'p95 latency' : `${a.metric} / min`, values: tail(series[key], SPARK_N), color: a.severity === 'down' ? 'down' : 'warn', last: null }] : [],
    sections: [], issues: a.text ? [{ kind: 'anomaly', level: a.severity === 'down' ? 'down' : 'warn', text: a.text }] : [],
    children: n ? [{ kind: 'node', id: n.id }] : [],
  };
}

// The floor timeline: every app's requests summed per bucket (red where any
// app returned 5xx), incident marks and prod deploy ticks across the window.
export function fleetTimeline(s, t) {
  const now = t?.window?.now || Date.now();
  const windowMs = t?.window?.ms || 86400e3;
  const t0 = now - windowMs;
  const u = (at) => Math.max(0, Math.min(1, (at - t0) / windowMs));
  const slugs = new Set(s.nodes.map((n) => n.slug));
  const series = Object.entries(t?.series || {}).filter(([slug, x]) => slugs.has(slug) && x?.ts?.length);
  let buckets = [];
  let apps = 0;
  if (series.length) {
    const n = series[0][1].ts.length; const per = Math.max(1, Math.ceil(n / 48));
    const req = new Array(n).fill(0); const bad = new Array(n).fill(0);
    for (const [, x] of series) {
      let any = false;
      for (let i = 0; i < n; i += 1) { const v = Number(x.req?.[i]) || 0; req[i] += v; bad[i] += Number(x.s5xx?.[i]) || 0; if (v) any = true; }
      if (any) apps += 1;
    }
    for (let i = 0; i < n; i += per) {
      let r = 0; let e = 0; const m = Math.min(n, i + per);
      for (let k = i; k < m; k += 1) { r += req[k]; e += bad[k]; }
      buckets.push({ ts: series[0][1].ts[i], req: r / (m - i), s5xx: e });
    }
  }
  const marks = s.events.filter((e) => e.at >= t0 && e.level !== 'busy').map((e) => ({ u: u(e.at), level: e.level === 'ok' ? 'ok' : 'down', at: e.at, open: e.level === 'down' })).sort((a, b) => a.at - b.at).slice(-16);
  const deploys = s.nodes.filter((n) => n.deploy?.lastProdAt >= t0).map((n) => ({ u: u(n.deploy.lastProdAt), level: n.deploy.lastIsRollback ? 'warn' : 'ok', at: n.deploy.lastProdAt, slug: n.slug }));
  const req = buckets.reduce((a, b) => a + b.req, 0) * (windowMs / 60e3) / Math.max(1, buckets.length);
  const s5xx = buckets.reduce((a, b) => a + b.s5xx, 0);
  return { windowLabel: t?.window?.label || '24h', buckets, marks, deploys, apps, req, s5xx };
}

function timelineCard(s, t) {
  const tl = fleetTimeline(s, t);
  const sim = t?.source === 'sim';
  return {
    kind: 'timeline', id: 'timeline', open: null,
    title: `timeline · ${tl.windowLabel}`,
    subtitle: `requests across ${tl.apps} app${tl.apps === 1 ? '' : 's'} · incidents · prod deploys${sim ? ' · simulated' : ''}`,
    tone: tl.marks.some((m) => m.open) ? 'down' : 'ok',
    rows: [
      ['requests', n0(tl.req)],
      ['5xx', n0(tl.s5xx)],
      ['incident marks', `${tl.marks.length}${tl.marks.filter((m) => m.open).length ? ` · ${tl.marks.filter((m) => m.open).length} open` : ''}`],
      ['prod deploys', tl.deploys.length ? `${tl.deploys.length} · last ${ago(Math.max(...tl.deploys.map((d) => d.at)))}` : 'none in window'],
    ],
    sparks: tl.buckets.length ? [{ label: `requests / min · all apps${sim ? ' · sim' : ''}`, values: tl.buckets.map((b) => b.req), color: 'ok', last: null }] : [],
    sections: tl.deploys.length ? [{ title: 'deploys', rows: tl.deploys.slice(-6).map((d) => [d.slug, `${d.level === 'warn' ? 'rollback · ' : ''}${ago(d.at)}`]) }] : [],
    issues: [], children: [],
  };
}

export function fleetInspector(s, t, sel) {
  if (!s) return null;
  if (!sel || sel.kind === 'center') return centerCard(s, t);
  if (sel.kind === 'timeline') return timelineCard(s, t);
  if (sel.kind === 'node') { const n = s.nodes.find((x) => x.id === sel.id); return n ? nodeCard(s, t, n) : null; }
  if (sel.kind === 'host') { const h = s.hosts.find((x) => x.id === sel.id); return h ? hostCard(s, t, h) : null; }
  if (sel.kind === 'flow') return flowCard(s, t, sel.id);
  if (sel.kind === 'incident') return incidentCard(s, t, sel.id);
  if (sel.kind === 'anomaly') return anomalyCard(s, t, sel.id);
  return null;
}
